import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import './UtmBuilder.sass'
import ModulePanel from '../../../ui/ModulePanel/ModulePanel'
import StatusMessage, { useStatusMessage } from '../../../ui/StatusMessage/StatusMessage'
import { createForm } from '../../../utils/newforms/createForm'
import FormRender from '../../../utils/newforms/render/FormRender'
import Button from '../../../ui/Button/Button'
import isFormValid from '../../../utils/newforms/validation/isFormValid'
import { buildCampaignLinkPreview, PREVIEW_STATE } from '../../../utils/utm/buildCampaignLinkPreview'
import { UTM_BUILDER_DEFAULTS } from '../../../config/utmBuilderDefaults'
import buildUtmBuilderFormValues from '../../../api/marketing/buildUtmBuilderFormValues'
import createCampaignLink from '../../../api/marketing/createCampaignLink'
import updateCampaignLink from '../../../api/marketing/updateCampaignLink'
import getCampaignLink from '../../../api/marketing/getCampaignLink'
import mapCampaignLinkToBuilderState from '../../../api/marketing/mapCampaignLinkToBuilderState'
import loadSelectableBrands from '../../../api/marketing/loadSelectableBrands'
import buildRelatedProductSearchFilters from '../../../api/marketing/buildRelatedProductSearchFilters'
import loadRelatedProductCatalog from '../../../api/marketing/loadRelatedProductCatalog'
import loadSelectablePromoSchedules from '../../../api/marketing/loadSelectablePromoSchedules'
import { isProductMatchingFilters } from '../../../api/marketing/searchRelatedProducts'
import enrichRelatedProductFromCatalog from '../../../api/marketing/enrichRelatedProductFromCatalog'
import appendBrandFromRelatedProduct from '../../../api/marketing/appendBrandFromRelatedProduct'
import {
  areSameBrandSets,
  resolveBrandsFromCategoryNode,
} from '../../../api/marketing/resolveBrandsFromCategoryNode'
import RelatedBrandsMultiSelect from './RelatedBrandsMultiSelect'
import RelatedCategoryPicker from './RelatedCategoryPicker'
import RelatedProductSearch from './RelatedProductSearch'
import RelatedPromoPicker from './RelatedPromoPicker'
import UtmPreviewPanel from './UtmPreviewPanel'
import UtmLinkForm from './UtmLinkForm'
import useLazyFirestoreCatalog from './useLazyFirestoreCatalog'
import Spinner from '../../../ui/Spinner/Spinner'
import { AuthContext } from '../../../App'
import { isMarketingOnlyUser } from '../../../config/accessControl'

const CAMPAIGN_DATE_HINT =
  'Для stories, posts, ads без повʼязаної промо. Якщо промо вибрана, дата береться зі старту акції.'

function UtmBuilder({ mode = 'create', campaignLinkId = null, onSaved }) {
  const { user } = useContext(AuthContext)
  const history = useHistory()
  const marketingOnly = isMarketingOnlyUser(user?.uid)
  const mainFormFields = marketingOnly ? ['title', 'destinationUrl'] : ['title', 'owner', 'destinationUrl']
  const [form, setForm] = useState(() =>
    createForm({
      formPattern: new UtmLinkForm(UTM_BUILDER_DEFAULTS),
    })
  )
  const [showErrors, setShowErrors] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingRecord, setIsLoadingRecord] = useState(mode === 'edit')
  const [statusMessage, setStatusMessage] = useStatusMessage()
  const [savedShortLink, setSavedShortLink] = useState(null)
  const [relatedBrands, setRelatedBrands] = useState([])
  const [relatedCategory, setRelatedCategory] = useState(null)
  const [brandsSuggestion, setBrandsSuggestion] = useState(null)
  const [brandOptions, setBrandOptions] = useState([])
  const [isBrandsLoading, setIsBrandsLoading] = useState(true)
  const [brandsLoadError, setBrandsLoadError] = useState('')

  const {
    data: productSearchIndex,
    isLoading: isProductsLoading,
    loadError: productsLoadError,
    load: loadProductCatalog,
  } = useLazyFirestoreCatalog({
    loadCatalog: loadRelatedProductCatalog,
    errorMessage: 'Не вдалося завантажити каталог товарів',
    logLabel: '[UtmBuilder] Failed to load products:',
  })

  const {
    data: promoOptions,
    isLoading: isPromosLoading,
    loadError: promosLoadError,
    load: loadPromoCatalog,
  } = useLazyFirestoreCatalog({
    loadCatalog: loadSelectablePromoSchedules,
    errorMessage: 'Не вдалося завантажити список промо / акцій',
    logLabel: '[UtmBuilder] Failed to load promo schedules:',
  })

  const [relatedProduct, setRelatedProduct] = useState(null)
  const [relatedPromo, setRelatedPromo] = useState(null)
  const [hasLoadedRecord, setHasLoadedRecord] = useState(false)

  const productFilters = useMemo(
    () => buildRelatedProductSearchFilters(relatedBrands, relatedCategory),
    [relatedBrands, relatedCategory]
  )

  const canBrowseProductOnFocus = useMemo(
    () => relatedBrands.length > 0 && Boolean(relatedCategory?.related_product_filter),
    [relatedBrands, relatedCategory]
  )

  const formValues = useMemo(
    () =>
      buildUtmBuilderFormValues({
        form,
        relatedBrands,
        relatedCategory,
        relatedProduct,
        relatedPromo,
      }),
    [form, relatedBrands, relatedCategory, relatedProduct, relatedPromo]
  )
  const preview = useMemo(
    () => buildCampaignLinkPreview(formValues, UTM_BUILDER_DEFAULTS),
    [formValues]
  )
  const isLinkActive = Boolean(form?.isActive?.value)

  useEffect(() => {
    if (mode !== 'edit' || !campaignLinkId) {
      return undefined
    }

    let isMounted = true
    setIsLoadingRecord(true)

    getCampaignLink(campaignLinkId)
      .then((result) => {
        if (!isMounted) {
          return
        }

        if (!result.ok) {
          setStatusMessage({
            show: true,
            type: 'fail',
            message: result.message || 'Не вдалося завантажити посилання',
            closeAfter: 6000,
          })
          return
        }

        const responsible = result.data?.responsible || result.data?.owner || ''

        if (marketingOnly && responsible !== user?.uid) {
          setStatusMessage({
            show: true,
            type: 'fail',
            message: 'Немає доступу до редагування цього посилання',
            closeAfter: 6000,
          })
          history.push('/marketing/utm')
          return
        }

        const builderState = mapCampaignLinkToBuilderState(result.data)

        setForm(
          createForm({
            formPattern: new UtmLinkForm(UTM_BUILDER_DEFAULTS),
            formData: builderState.formData,
          })
        )
        setRelatedBrands(builderState.relatedBrands)
        setRelatedCategory(builderState.relatedCategory)
        setRelatedProduct(builderState.relatedProduct)
        setRelatedPromo(builderState.relatedPromo)
        setSavedShortLink(builderState.savedShortLink)
        setHasLoadedRecord(true)
      })
      .catch((error) => {
        console.error('[UtmBuilder] Failed to load campaign link:', error)

        if (!isMounted) {
          return
        }

        setStatusMessage({
          show: true,
          type: 'fail',
          message: 'Не вдалося завантажити посилання',
          closeAfter: 6000,
        })
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingRecord(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [mode, campaignLinkId, setStatusMessage, marketingOnly, user?.uid, history])

  useEffect(() => {
    if (mode === 'create') {
      setSavedShortLink(null)
    }
  }, [formValues, mode])

  useEffect(() => {
    if (!marketingOnly || !user?.uid) {
      return
    }

    setForm((currentForm) => {
      const currentOwner = currentForm?.owner?.value

      if (currentOwner === user.uid) {
        return currentForm
      }

      return {
        ...currentForm,
        owner: {
          ...currentForm.owner,
          value: user.uid,
          valid: true,
          touched: true,
          error: '',
        },
      }
    })
  }, [marketingOnly, user?.uid])

  useEffect(() => {
    let isMounted = true

    setIsBrandsLoading(true)
    setBrandsLoadError('')

    loadSelectableBrands()
      .then((brands) => {
        if (!isMounted) {
          return
        }

        setBrandOptions(brands)
      })
      .catch((error) => {
        console.error('[UtmBuilder] Failed to load brands:', error)

        if (!isMounted) {
          return
        }

        setBrandsLoadError('Не вдалося завантажити список брендів')
      })
      .finally(() => {
        if (isMounted) {
          setIsBrandsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    if (mode !== 'edit' || !hasLoadedRecord) {
      return
    }

    if (relatedProduct) {
      loadProductCatalog()
    }

    if (relatedPromo) {
      loadPromoCatalog()
    }
  }, [mode, hasLoadedRecord, relatedProduct, relatedPromo, loadProductCatalog, loadPromoCatalog])

  useEffect(() => {
    if (!relatedProduct || !productSearchIndex?.length) {
      return
    }

    const enrichedProduct = enrichRelatedProductFromCatalog(relatedProduct, productSearchIndex)

    if (!relatedProduct.brandId && enrichedProduct.brandId) {
      setRelatedProduct(enrichedProduct)
      return
    }

    if (!enrichedProduct.brandId) {
      return
    }

    if (!isProductMatchingFilters(enrichedProduct, productFilters)) {
      setRelatedProduct(null)
    }
  }, [relatedProduct, productFilters, productSearchIndex])

  useEffect(() => {
    if (!relatedPromo?.id || relatedPromo.brandId || !promoOptions?.length) {
      return
    }

    const matchedPromo = promoOptions.find((option) => option.id === relatedPromo.id)

    if (!matchedPromo?.brandId) {
      return
    }

    setRelatedPromo((current) => (current ? { ...current, brandId: matchedPromo.brandId } : current))
  }, [relatedPromo, promoOptions])

  useEffect(() => {
    if (!relatedPromo?.brandId || brandOptions.length === 0) {
      return
    }

    setRelatedBrands((currentBrands) => appendBrandFromRelatedProduct(currentBrands, relatedPromo, brandOptions))
  }, [relatedPromo, brandOptions])

  const handleRelatedProductChange = (product) => {
    setRelatedProduct(product)

    if (!product) {
      return
    }

    setRelatedBrands((currentBrands) => appendBrandFromRelatedProduct(currentBrands, product, brandOptions))
  }

  const handleBrandsSuggestion = (brandIds) => {
    const brandsForCategory = resolveBrandsFromCategoryNode(brandIds, brandOptions)

    if (brandsForCategory.length === 0) {
      return
    }

    if (relatedBrands.length === 0) {
      setRelatedBrands(brandsForCategory)
      return
    }

    if (areSameBrandSets(relatedBrands, brandsForCategory)) {
      return
    }

    setBrandsSuggestion({
      brands: brandsForCategory,
      label: brandsForCategory.map((brand) => brand.name).join(', '),
    })
  }

  const handleReplaceBrandsSuggestion = () => {
    if (!brandsSuggestion) {
      return
    }

    setRelatedBrands(brandsSuggestion.brands)
    setBrandsSuggestion(null)
  }

  const onSubmit = async () => {
    if (!isFormValid({ form, omitFields: marketingOnly ? ['owner'] : [] })) {
      setShowErrors(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return setStatusMessage({
        show: true,
        type: 'fail',
        message: 'Будь ласка, заповніть обовʼязкові поля',
        closeAfter: 5000,
      })
    }

    if (preview?.previewState !== PREVIEW_STATE.VALID) {
      const invalidMessage = preview?.invalidCombinationMessage

      return setStatusMessage({
        show: true,
        type: 'fail',
        message:
          invalidMessage?.message ||
          (mode === 'edit'
            ? 'Перевірте поля форми перед збереженням змін'
            : 'Перевірте поля форми перед створенням посилання'),
        closeAfter: 6000,
      })
    }

    if (mode === 'edit' && !campaignLinkId) {
      return setStatusMessage({
        show: true,
        type: 'fail',
        message: 'Не вдалося визначити посилання для збереження',
        closeAfter: 5000,
      })
    }

    setShowErrors(false)
    setIsSubmitting(true)

    const payload = marketingOnly
      ? {
          ...formValues,
          owner: user.uid,
        }
      : formValues

    const result =
      mode === 'edit'
        ? await updateCampaignLink(campaignLinkId, payload)
        : await createCampaignLink(payload)

    setIsSubmitting(false)

    if (!result.ok) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return setStatusMessage({
        show: true,
        type: 'fail',
        message: result.message || 'Не вдалося зберегти посилання',
        closeAfter: 6000,
      })
    }

    setSavedShortLink({
      short_slug: result.data.shortSlug,
      short_path: result.data.shortPath,
      short_url: result.data.shortUrl,
    })

    setStatusMessage({
      show: true,
      type: 'success',
      message: mode === 'edit' ? 'Зміни збережено' : 'Посилання збережено',
      closeAfter: 6000,
    })
    if (typeof onSaved === 'function') {
      onSaved(result.data)
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="UtmBuilder">
      {statusMessage.show && (
        <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
      )}

      {isLoadingRecord ? (
        <Spinner type="module" />
      ) : (
      <div className="UtmBuilder-Grid">
        <ModulePanel title={mode === 'edit' ? 'Редагування посилання / UTM' : 'Нове посилання / UTM'}>
          <div className="UtmBuilder-ActiveInfo">
            <FormRender sections={[{ fields: ['isActive'] }]} form={form} setForm={setForm} errors={showErrors} />
            <p className="UtmBuilder-Hint">
              Основне посилання працює постійно. Коротке активується цією опцією і робить редірект на основне
              посилання цільової сторінки.
            </p>
          </div>

          <section className="form-section">
            <h3 className="form-section-title">Основне</h3>
            <div className="form-section-content">
              <div className="UtmBuilder-MainFields">
                <FormRender
                  sections={[{ fields: mainFormFields }]}
                  form={form}
                  setForm={setForm}
                  errors={showErrors}
                />
              </div>
            </div>
          </section>

          <section className="form-section">
            <h3 className="form-section-title">UTM-параметри</h3>
            <div className="form-section-content">
              <div className="UtmBuilder-UtmParamsRow">
                <FormRender
                  sections={[{ fields: ['channel', 'format', 'direction'] }]}
                  form={form}
                  setForm={setForm}
                  errors={showErrors}
                />
                <div className="UtmBuilder-CampaignDateCell">
                  <FormRender
                    sections={[{ fields: ['campaign_date'] }]}
                    form={form}
                    setForm={setForm}
                    errors={showErrors}
                  />
                  <button
                    type="button"
                    className="UtmBuilder-CampaignDateHintInfo"
                    aria-label={CAMPAIGN_DATE_HINT}
                  >
                    i
                    <span className="UtmBuilder-CampaignDateHintTooltip" role="tooltip">
                      {CAMPAIGN_DATE_HINT}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="form-section">
            <h3 className="form-section-title">Звʼязки, опціонально</h3>
            <div className="form-section-content">
              <div className="UtmBuilder-Relations">
                <RelatedBrandsMultiSelect
                  brands={brandOptions}
                  isLoading={isBrandsLoading}
                  loadError={brandsLoadError}
                  value={relatedBrands}
                  onChange={(brands) => {
                    setRelatedBrands(brands)
                    setBrandsSuggestion(null)
                  }}
                />
                {brandsSuggestion ? (
                  <div className="UtmBuilder-BrandsSuggestion">
                    <span>Замінити бренди на: {brandsSuggestion.label}?</span>
                    <span className="UtmBuilder-BrandsSuggestionActions">
                      <button
                        type="button"
                        className="UtmBuilder-BrandsSuggestionButton UtmBuilder-BrandsSuggestionButton_add"
                        onClick={handleReplaceBrandsSuggestion}
                      >
                        Замінити
                      </button>
                      <button
                        type="button"
                        className="UtmBuilder-BrandsSuggestionButton UtmBuilder-BrandsSuggestionButton_skip"
                        onClick={() => setBrandsSuggestion(null)}
                      >
                        Пропустити
                      </button>
                    </span>
                  </div>
                ) : null}
                <div className="UtmBuilder-Relations_cols3">
                  <RelatedCategoryPicker
                    value={relatedCategory}
                    onChange={(category) => {
                      setRelatedCategory(category)
                      setBrandsSuggestion(null)
                    }}
                    brandIds={relatedBrands.map((brand) => brand.id)}
                    onBrandsSuggestion={handleBrandsSuggestion}
                  />
                  <RelatedProductSearch
                    value={relatedProduct}
                    onChange={handleRelatedProductChange}
                    searchIndex={productSearchIndex || []}
                    filters={productFilters}
                    isLoading={isProductsLoading}
                    loadError={productsLoadError}
                    onLoadRequest={loadProductCatalog}
                    canBrowseOnFocus={canBrowseProductOnFocus}
                  />
                  <RelatedPromoPicker
                    value={relatedPromo}
                    onChange={setRelatedPromo}
                    options={promoOptions || []}
                    isLoading={isPromosLoading}
                    loadError={promosLoadError}
                    onLoadRequest={loadPromoCatalog}
                  />
                </div>
              </div>
            </div>
          </section>

          <div className="UtmBuilder-Buttons">
            <Button
              title={mode === 'edit' ? 'Зберегти зміни' : 'Створити посилання'}
              theme="solid"
              fill="accent"
              iconPosition="right"
              size={32}
              isLoading={isSubmitting}
              onClick={onSubmit}
            />
          </div>
        </ModulePanel>

        <ModulePanel title="Попередній перегляд">
          <UtmPreviewPanel preview={preview} isLinkActive={isLinkActive} savedShortLink={savedShortLink} />
        </ModulePanel>
      </div>
      )}
    </div>
  )
}

export default UtmBuilder
